import React from 'react';
import { Notification } from '../../types';
import { FiCheck, FiCheckCircle, FiTrash2, FiX, FiClock, FiShoppingCart, FiUser, FiCalendar, FiBell } from 'react-icons/fi';
import Icon from '../common/Icon';

interface NotificationDropdownProps {
  notifications: Notification[];
  loading: boolean;
  onMarkAsRead: (id: number) => void;
  onMarkAllAsRead: () => void;
  onDelete: (id: number) => void;
  onClose: () => void;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  notifications,
  loading,
  onMarkAsRead,
  onMarkAllAsRead,
  onDelete,
  onClose
}) => {
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'order_status':
        return <Icon component={FiShoppingCart} className="w-4 h-4" />;
      case 'customer_status':
        return <Icon component={FiUser} className="w-4 h-4" />;
      case 'follow_up':
        return <Icon component={FiCalendar} className="w-4 h-4" />;
      default:
        return <Icon component={FiBell} className="w-4 h-4" />;
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-hidden flex flex-col">
      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
        <div className="flex items-center gap-2">
          {notifications.filter(n => !n.is_read).length > 0 && (
            <button
              onClick={onMarkAllAsRead}
              className="text-xs text-indigo-600 hover:text-indigo-800"
              title="Mark all as read"
            >
              Mark all read
            </button>
          )}
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            title="Close"
          >
            <Icon component={FiX} className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="overflow-y-auto flex-1">
        {loading ? (
          <div className="p-4 text-center text-sm text-gray-500">Loading...</div>
        ) : notifications.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500">No notifications</div>
        ) : (
          <div className="divide-y divide-gray-200">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`p-3 hover:bg-gray-50 ${!notification.is_read ? 'bg-blue-50' : ''}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`flex-shrink-0 mt-0.5 ${!notification.is_read ? 'text-blue-600' : 'text-gray-400'}`}>
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${!notification.is_read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                      {notification.title}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">{notification.message}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Icon component={FiClock} className="w-3 h-3" />
                        {formatTime(notification.created_at)}
                      </span>
                      {!notification.is_read && (
                        <button
                          onClick={() => onMarkAsRead(notification.id)}
                          className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                          title="Mark as read"
                        >
                          <Icon component={FiCheck} className="w-3 h-3" />
                          Mark read
                        </button>
                      )}
                      <button
                        onClick={() => onDelete(notification.id)}
                        className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1 ml-auto"
                        title="Delete"
                      >
                        <Icon component={FiTrash2} className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationDropdown;

